'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { useLanguage } from '@/app/context/language-context';
import { useRouter } from 'next/navigation';

const faqs = [
  // {
  //   id: 1,
  //   question: 'What documents do I need to buy property in Dubai?',
  //   answer:
  //     'To purchase property in Dubai, you\'ll need a copy of your passport, Emirates ID (for UAE residents), and proof of address. For the transaction, you\'ll need a reservation form, sale purchase agreement, and NOC from the developer. Additional documents may be required depending on your specific situation.',
  // },
  // {
  //   id: 2,
  //   question: 'Can foreigners buy property in Dubai?',
  //   answer:
  //     'Yes, foreigners (non-UAE nationals) can buy freehold property in designated areas in Dubai. These areas include popular locations such as Downtown Dubai, Palm Jumeirah, Dubai Marina, and many others. Foreigners can purchase apartments, villas, or land in these designated areas and obtain freehold ownership rights.',
  // },
  // {
  //   id: 3,
  //   question:
  //     'What are the additional costs involved in buying property in Dubai?',
  //   answer:
  //     'When purchasing property in Dubai, you should budget for additional costs including: 4% Dubai Land Department transfer fee, 2% real estate agent commission (typically), 0.25% mortgage registration fee (if applicable), AED 2,000-4,000 for title deed issuance, and potentially maintenance fees, service charges, and property insurance.',
  // },

  {
    id: 1,
    question:
      'What are the requirements for buying property in Dubai as Expats?',
    answer:
      'Expats can buy property in Dubai in designated areas known as freehold areas. They need to ensure they meet the eligibility criteria set by the Dubai Land Department and obtain necessary approvals.',
  },
  {
    id: 2,
    question: 'Are there any taxes on property ownership in Dubai?',
    answer:
      'Currently, there are no property taxes in Dubai, including no capital gains tax or income tax on rental income. However, there are some fees associated with property ownership, such as registration fees and service charges.',
  },
  {
    id: 3,
    question: 'What is the role of a real estate agent in Dubai?',
    answer:
      'Real estate agents in Dubai help buyers, sellers, landlords, and tenants navigate the property market. They provide expertise on market trends, assist with property searches, facilitate negotiations, and handle documentation and legal procedures.',
  },
  {
    id: 4,
    question:
      'Are there any regulations regarding property maintenance in Dubai?',
    answer:
      'Yes, property owners and landlords are responsible for maintaining their properties in Dubai. They must ensure that the property complies with health, safety, and environmental standards. Tenants are typically responsible for minor maintenance tasks unless otherwise specified in the tenancy agreement.',
  },
  {
    id: 5,
    question: 'What are the visa options for property investors in Dubai?',
    answer:
      'Property investors in Dubai may be eligible for residency visas, such as the Dubai Investor Visa or the Golden Visa, depending on the value of their investment. These visas often come with specific criteria and requirements set by the authorities.',
  },
  {
    id: 6,
    question: 'What are the current market trends in Dubai real estate?',
    answer:
      'Market trends in Dubai real estate can vary based on factors like economic conditions, government policies, and global influences. It\'s advisable to stay updated on market reports, forecasts, and expert opinions to make informed decisions.',
  },
];

export default function FAQSection() {
  const { t } = useLanguage();
  const router = useRouter();

  const [openIndex, setOpenIndex] = useState(null);
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  };

  const contentVariants = {
    hidden: { opacity: 0, height: 0 },
    visible: {
      opacity: 1,
      height: 'auto',
      transition: { duration: 0.3 },
    },
  };

  return (
    <motion.section
      ref={ref}
      className='py-16 px-4 bg-gray-50 dark:bg-gray-800'
      initial='hidden'
      animate={inView ? 'visible' : 'hidden'}
      variants={containerVariants}
    >
      <div className='max-w-4xl mx-auto'>
        <div className='text-center mb-12'>
          <motion.div
            className='inline-flex items-center justify-center w-16 h-16 rounded-full bg-primarycolor/10 mb-4'
            variants={itemVariants}
          >
            <HelpCircle className='w-8 h-8 text-primarycolor' />
          </motion.div>
          <motion.h2
            className='text-3xl font-bold text-gray-900 dark:text-white mb-4'
            variants={itemVariants}
          >
            {t('faq')}
          </motion.h2>
          <motion.p
            className='text-gray-600 dark:text-gray-400 max-w-2xl mx-auto'
            variants={itemVariants}
          >
            {t('faqDescription')}
          </motion.p>
        </div>
        <div className='space-y-4'>
          {faqs.map((faq, index) => (
            <motion.div
              key={faq.id}
              className='bg-white dark:bg-gray-900 rounded-xl overflow-hidden shadow-md'
              variants={itemVariants}
            >
              <button
                className='flex items-center justify-between w-full p-6 text-left'
                onClick={() => toggleFAQ(index)}
                aria-expanded={openIndex === index}
              >
                <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
                  {faq.question}
                </h3>
                <motion.div
                  animate={{ rotate: openIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className='flex-shrink-0 ml-4'
                >
                  <ChevronDown className='w-5 h-5 text-gray-500 dark:text-gray-400' />
                </motion.div>
              </button>
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial='hidden'
                    animate='visible'
                    exit='hidden'
                    variants={contentVariants}
                    className='overflow-hidden'
                  >
                    <div className='p-6 pt-0 text-gray-600 dark:text-gray-400 border-t border-gray-100 dark:border-gray-800'>
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* Contact CTA */}
        <motion.div className='mt-12 text-center' variants={itemVariants}>
          <p className='text-gray-600 dark:text-gray-400 mb-4'>
            {t('cantFindAnswer')}
          </p>
          <motion.button
            className='px-6 py-3 bg-primarycolor text-white rounded-md hover:bg-secondarycolor hover:text-black transition-colors'
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => router.push('/contact-us')}
          >
            {t('contactOurTeam')}
          </motion.button>
        </motion.div>
      </div>
    </motion.section>
  );
}
